import * as React from 'react';

import { Translate } from 'react-localize-redux';

import { Dispatch } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { goBack, CallHistoryMethodAction } from 'connected-react-router';

import styled from 'styled-components';

import { getWidth } from '@components/styles';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import ButtonGroup from '@components/basics/button-group';
import UnselectedIcon from '@components/basics/icons/unselected';
import DashboardContainer from '@components/layout/dashboard-container';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import TextareaWithCount from '@components/form/textareawithcount';
import MotionOpacity from '@components/motion/motion-opacity';
import { motion } from 'framer-motion';
import { CLOSE_ACCOUNT_POLL_ITEMS, UNSUBSCRIBE_POLL_ITEMS } from './poll-items';
import { CancelSubscription } from '@webapp/store/subscription/action-creators';
import AppState from '@webapp/store/state';

interface PollPageProps {
  className?: string;
  goBack: () => CallHistoryMethodAction;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
});

const PollPage: React.FC<PollPageProps> = (props) => {
  const [selectedItem, setSelectedItem] = React.useState<string>('');
  const [otherText, setOtherText] = React.useState<string>('');
  const location = useLocation();
  const dispatch = useDispatch();

  const artistId = useSelector((state: AppState) => state.artist.active?.id);

  const getSectionName = () => {
    if (location.pathname.endsWith('/close-account')) {
      return 'close-account';
    }
    if (location.pathname.endsWith('/unsubscribe/poll')) {
      return 'unsubscribe';
    }
  };

  const getTitle = () => {
    switch (getSectionName()) {
      case 'unsubscribe':
        return 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.TITLE';
      case 'close-account':
        return 'USER_PROFILE.CLOSE_ACCOUNT.TITLE';

      default:
        break;
    }
  };

  const getSubtitle = () => {
    switch (getSectionName()) {
      case 'unsubscribe':
        return 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.TEXT';
      case 'close-account':
        return 'USER_PROFILE.CLOSE_ACCOUNT.TEXT';

      default:
        break;
    }
  };

  const getPollTitle = () => {
    switch (getSectionName()) {
      case 'unsubscribe':
        return 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.TITLE';
      case 'close-account':
        return 'USER_PROFILE.CLOSE_ACCOUNT.POLL.TITLE';

      default:
        break;
    }
  };

  const getContent = () => {
    switch (getSectionName()) {
      case 'unsubscribe':
        return UNSUBSCRIBE_POLL_ITEMS;
      case 'close-account':
        return CLOSE_ACCOUNT_POLL_ITEMS;
      default:
        return [];
    }
  };

  // TODO: 4nje1ru - Integrate handleDowngrade
  const handleDowngrade = () => {
    if (artistId) {
      return dispatch(new CancelSubscription(artistId));
    }
  };

  return (
    <DashboardContainer className={props.className}>
      <Wrapper animate>
        <Flex flexDirection="column" gap={10} className="mb-30">
          <Typography type="subheader" customFont="semi-bold">
            <Translate id={getTitle()} />
          </Typography>
          <Typography className="text" type="title" weight="400">
            <Translate id={getSubtitle()} />
          </Typography>
        </Flex>
        <PollWrapper className="mb-40">
          <Typography type="section-title" customFont="medium">
            <Translate id={getPollTitle()} />
          </Typography>
          {getContent().map((item) => (
            <Flex
              className={`poll-row ${
                selectedItem && selectedItem !== item.value
                  ? 'has-selected'
                  : ''
              }`}
              key={item.value}
              gap={10}
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setSelectedItem(item.value)}
            >
              <Typography weight={selectedItem === item.value ? '700' : '400'}>
                {/* TODO: DEVTEAM-845 - Integrate current plan */}
                <Translate id={item.label} data={{ planName: 'trac Pro' }} />
              </Typography>
              {selectedItem === item.value ? (
                <CompletedIcon className="icon" />
              ) : (
                <UnselectedIcon className="icon" />
              )}
            </Flex>
          ))}
          <Flex
            className={`poll-row ${
              selectedItem && selectedItem !== 'other' ? 'has-selected' : ''
            }`}
            gap={10}
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setSelectedItem('other')}
          >
            <Typography weight={selectedItem === 'other' ? '700' : '400'}>
              {/* TODO: DEVTEAM-845 - Integrate current plan */}
              <Translate
                id="COMMON.POLL.OTHER"
                data={{ planName: 'trac Pro' }}
              />
            </Typography>
            {selectedItem === 'other' ? (
              <CompletedIcon className="icon" />
            ) : (
              <UnselectedIcon className="icon" />
            )}
          </Flex>
          {selectedItem === 'other' && (
            <MotionOpacity>
              <TextareaWithCount
                maxLength={300}
                value={otherText}
                label={<Translate id="COMMON.POLL.OTHER.TEXT_AREA" />}
                onChange={(value) => setOtherText(value)}
              />
            </MotionOpacity>
          )}
        </PollWrapper>
        <ButtonGroup
          disabled={
            !selectedItem || (selectedItem === 'other' && otherText.length <= 0)
          }
          alertButton
          onCancel={() => props.goBack()}
          onChange={handleDowngrade}
          customRightText={<Translate id="COMMON.UNSUBSCRIBE" />}
        />
      </Wrapper>
    </DashboardContainer>
  );
};

export default connect(null, mapDispatchToProps)(styled(PollPage)``);

const Wrapper = styled(motion.div)`
  margin: auto;
  max-width: ${getWidth(6)};

  @media ${({ theme }) => theme.device.tablet} {
    max-width: ${getWidth(10)};
  }

  .text {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const PollWrapper = styled.div`
  padding: 15px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  display: grid;
  grid-gap: 10px;

  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
    grid-gap: 20px;
  }

  .poll-row {
    cursor: pointer;
    &.has-selected {
      opacity: 0.5;
    }
  }

  .icon {
    width: 24px;
    height: 24px;
  }
`;
